import type { ObjectForm } from "@pdfking/forms"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import { Map, Range, RecordCreate } from "@pdfking/tfunc"

import { range_for } from "../../controller/RangeFor"
import { IDMixin } from "../../mixins/IDMixin"
import { dom_id } from "../dispatch/DomId"
import { DjInput } from "../fields/DjInput"
import { DjSelect } from "../fields/DjSelect"
import { FieldMixin } from "../mixins/FieldMixin"
import { AtomFormMixin } from "../mixins/FormMixin"
import { NodeMixin } from "../mixins/NodeMixin"
import { sibling_file_id } from "./SiblingFileId"

// How many blank pages the select offers. A blank row's range is a count, not a
// window onto a document, so there is no real bound to read off anything — this
// is only the affordance. A value from outside the list still shows: DjSelect
// keeps an unrecognised current value as an option of its own.
const BLANK_CHOICES = 16

export const FileRange = CreateRenderFunction({
  mixins: [NodeMixin, FieldMixin, IDMixin, AtomFormMixin] as const,
  computed: {
    // Empty on a blank-pages row: it names no file (see FileRows), so there is
    // no document to take a range out of.
    filename(): string {
      return sibling_file_id(this.parent as ObjectForm<Record<string, unknown>> | undefined)
    },
    page_count(): number {
      return (this.filename && this.ctx.file_media[this.filename]?.page_count) || 0
    },
    initials(): string[] {
      const pages = this.page_count
      return this.page_count ? (pages > 1 ? [`1-${pages}`, "1"] : ["1"]) : []
    },
  },
  render() {
    // A blank row picks *how many* blank pages — a count out of a short list,
    // not a range to type against a document that isn't there. What it commits
    // is still an ordinary range string, so nothing downstream has to know the
    // row was filled in a different way.
    if (!this.filename) {
      return (
        <DjSelect
          choices={RecordCreate(
            Map(n => [range_for(n), String(n)] as const, Range(1, BLANK_CHOICES))
          )}
          disabled={this.disabled}
          dom_id={dom_id(this)}
          error={this.node!.error}
          input={this.node!.input}
          onselect={(value: string | string[]) => this.node!.validate(value)}
        />
      )
    }

    const initials = this.initials
    return (
      <DjInput
        disabled={this.disabled}
        error={this.node!.error}
        dom_id={dom_id(this)}
        input={(this.node!.input as string) ?? ""}
        oncommit={v => this.node!.validate(v || initials[0])}
        placeholder={initials[0]}
        suggestions={RecordCreate(initials, initials)}
      />
    )
  },
})
